// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import deleteWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/DeleteWishList";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import Notify from "@vanmeter/App/Components/Notify";
import Skeleton from "@vanmeter/App/Components/Skeleton/Skeleton";
import RecentWishListsCondensedView from "@vanmeter/App/Components/WishList/RecentWishListsCondensedView";
import CardListHeading from "@vanmeter/Overrides/Components/CardListHeading";
import WishListCard, { WishListCardStyles } from "@vanmeter/Overrides/Components/WishListCard";
import { CenteringWrapper } from "@vanmeter/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    // XNG-Changes
    isCondensedView = "isCondensedView",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        // XNG-Changes
        [fields.isCondensedView]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
    // XNG-Changes
    deleteWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    // XNG-Changes
    cardListHeadingStyles?: CardListStyles;
    cardListStyles?: CardListStyles;
    cardContainer?: CardContainerStyles;
    noWishListsGridItem?: GridItemProps;
    notify?: TypographyProps;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },
    },
};

// XNG-Changes
interface State {
    wishListToAction?: WishListModel;
    deleteListModalIsOpen?: boolean;
    isDeletingList?: boolean;
    isUpdating?: boolean;
    firstLoad?: boolean;
}

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props, State> {
    // <XNG-Changes>
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;
    // XNG-Change init the state
    constructor(props: Props) {
        super(props);
        this.state = {
            deleteListModalIsOpen: false,
            // XNG-Changes
            isDeletingList: false,
            isUpdating: false,
            firstLoad: true,
        };
    }
    // <XNG-Changes>

    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.recentWishListsDataView.value && prevState.firstLoad) {
            this.setState({ firstLoad: false });
        }
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.setState({ isUpdating: true });
        const _this = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                _this.setState({ isUpdating: false });
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.setState({ isUpdating: true });
        const _this = this;
        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                _this.setState({ isUpdating: false });
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    // <XNG-Changes>
    deleteButtonClickHandler = (wishList: WishListModel) => {
        this.setState({
            wishListToAction: wishList,
            deleteListModalIsOpen: true,
        });
    };

    deleteCancelHandler = () => {
        this.setState({ deleteListModalIsOpen: false });
    };

    deleteSubmitHandler = () => {
        if (this.state.wishListToAction) {
            this.setState({ isDeletingList: true });
            const _this = this;
            this.props.deleteWishList({
                wishListId: this.state.wishListToAction.id,
                onSuccess: this.onDeleteSuccess,
                onComplete(resultProps) {
                    _this.setState({ deleteListModalIsOpen: false, isDeletingList: false });
                    if (!resultProps.error) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    onDeleteSuccess = () => {
        this.context.addToast({ body: translate("List Deleted"), messageType: "success" });
        this.setState({ deleteListModalIsOpen: false, isDeletingList: false });
    };

    // </XNG-Changes>

    render() {
        const {
            canViewWishLists,
            recentWishListsDataView: { value: recentWishLists, isLoading },
            myListsPageNavLink,
        } = this.props;
        // XNG-Changes
        if (canViewWishLists && isLoading && this.state.firstLoad) {
            return <Skeleton height="280px" />;
        }
        if (canViewWishLists && (this.state.isUpdating || (!this.state.firstLoad && isLoading))) {
            return (
                <CenteringWrapper height={300}>
                    <LoadingSpinner />
                </CenteringWrapper>
            );
        }
        if (!this.props.canViewWishLists) {
            return null;
        }

        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        // <XNG-Changes>
        if (this.props.fields.isCondensedView) {
            return (
                <RecentWishListsCondensedView
                    isLoading={isLoading}
                    wishLists={recentWishLists}
                    myListsPageUrl={myListsPageUrl}
                />
            );
        }
        // </XNG-Changes>

        // XNG-Changes: Structure update
        return (
            <>
                <CardList data-test-selector="cardListRecentWishLists" extendedStyles={styles.cardListStyles}>
                    <CardListHeading
                        heading={translate("My Recent Lists")}
                        viewAllUrl={myListsPageUrl}
                        viewAllLabel={translate("View All Lists")}
                    />
                    {recentWishLists?.length ? (
                        recentWishLists.map(wishList => (
                            <CardContainer key={wishList.id} extendedStyles={styles.cardContainer}>
                                <GridItem {...styles.wishListCardGridItem}>
                                    <WishListCard
                                        wishList={wishList}
                                        addWishListToCart={this.props.addWishListToCart}
                                        extendedStyles={styles.wishListCard}
                                        toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                        toggleHide={() => this.toggleHideClickHandler(wishList)}
                                        // XNG-Changes
                                        deleteWishList={() => this.deleteButtonClickHandler(wishList)}
                                    />
                                </GridItem>
                            </CardContainer>
                        ))
                    ) : (
                        <GridItem width={12} {...styles.noWishListsGridItem}>
                            <Notify type="info" {...styles.notify}>
                                {siteMessage("WishLists_NoWishlistsFound")}
                            </Notify>
                        </GridItem>
                    )}
                </CardList>
                <TwoButtonModal
                    modalIsOpen={this.state.deleteListModalIsOpen || false}
                    headlineText={translate("Delete List")}
                    messageText={`${translate("Are you sure you want to delete")} ${
                        this.state.wishListToAction ? this.state.wishListToAction.name : ""
                    }?`}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Delete")}
                    onCancel={this.deleteCancelHandler}
                    onSubmit={this.deleteSubmitHandler}
                    submitTestSelector="myListsListSubmitDelete"
                    // XNG-Changes
                    {...{
                        isSubmitting: this.state.isDeletingList,
                    }}
                ></TwoButtonModal>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
            // XNG-Changes
            {
                name: fields.isCondensedView,
                displayName: "Home Page View",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
